/**
 * Cmarkdowndate Component
 * Inline date chip rendered from ISO 8601 strings in markdown content.
 * Shows a readable date+time label with a calendar icon; hover/click reveals
 * a popover with a mini calendar and relative-time subtitle.
 */
'use client';
import type { CSSProperties } from 'react';
import { useState, useMemo, useCallback } from 'react';
import { IconCalendar } from '@tabler/icons-react';
import { Cbutton } from '../Cbutton';
import { Cpopover } from '../Cpopover';
import { Ccalendarview } from '../Ccalendarview';
import { Cclockview } from '../Cclockview';
import { Ctext } from '../Ctext';
import { ButtonPreset } from '@/constants/componentPresets';
import { FontWeight } from '@/theme/typography';
import { ColorType } from '@/theme/colors';
import { zIndex } from '@/theme/zIndex';
import { formatReadableDate, formatRelativeTime } from '@/utils/time';
const POPOVER_AUTO_PLACEMENT = 'auto';
export interface CmarkdowndateProps {
    isoString: string;
    style?: CSSProperties;
}
export function Cmarkdowndate({ isoString, style }: CmarkdowndateProps) {
    // Callback ref → state so Cpopover always receives a live DOM element (not a stale ref snapshot)
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const anchorRefCallback = useCallback((el: HTMLSpanElement | null) => {
        setAnchorEl(el);
    }, []);
    // Separate hover/pinned so the click toggle doesn't race with mouseenter
    const [hovered, setHovered] = useState(false);
    const [pinned, setPinned] = useState(false);
    const visible = hovered || pinned;
    const handleMouseEnter = useCallback(() => setHovered(true), []);
    const handleMouseLeave = useCallback(() => setHovered(false), []);
    const handleClick = useCallback(() => setPinned((v) => !v), []);
    const handleClose = useCallback(() => {
        setHovered(false);
        setPinned(false);
    }, []);
    const date = useMemo(() => new Date(isoString), [isoString]);
    const readableDate = useMemo(() => formatReadableDate(date), [date]);
    const relativeTime = useMemo(() => formatRelativeTime(date), [date]);
    const hasTime = useMemo(() => date.getHours() !== 0 || date.getMinutes() !== 0, [date]);
    return (
      <span ref={anchorRefCallback} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{
            display: 'inline-block',
            verticalAlign: 'middle',
            ...style,
        }}>
        <Cbutton preset={ButtonPreset.Secondary} icon={IconCalendar} iconSize={13.6} label={readableDate} textSize={13.6} height={22} padding="0 7px" gap={4} onClick={handleClick}/>
        <Cpopover dataElScope="Cmarkdowndate/DatePopover" visible={visible} anchorElement={anchorEl} onClose={handleClose} placement={POPOVER_AUTO_PLACEMENT} align="center" gap={6} padding={12} maxWidth={206} springEnter dismissOnOutsideClick zIndexOverride={zIndex.popoverAboveModal}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
        }}>
            <Ctext size={14} weight={FontWeight.SemiBold} color={ColorType.White}>
              {readableDate}
            </Ctext>
            <Ctext size={12} weight={FontWeight.Regular} color={ColorType.White} opacity={0.5} style={{ marginBottom: hasTime ? 8 : 10 }}>
              {relativeTime}
            </Ctext>
            {hasTime && (<div style={{ marginBottom: 8 }}>
                <Cclockview date={date}/>
              </div>)}
            <Ccalendarview date={date}/>
          </div>
        </Cpopover>
      </span>
    );
}
